import { useCallback, useEffect, useMemo, useState } from "react";
import {
  clearNotifications,
  getNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  migrateLegacyNotifications,
  notificationEventName,
  removeNotification,
} from "../services/notificationService";

export default function useNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      setError("");
      const data = await getNotifications();
      setNotifications(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Notification refresh failed:", err);
      setError(err.message || "Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let active = true;

    async function initialise() {
      try {
        await migrateLegacyNotifications();
      } catch (_error) {
        // Migration is best-effort and must not block the notification center.
      }

      if (active) await refresh();
    }

    initialise();

    const handleChange = () => refresh();
    window.addEventListener(notificationEventName, handleChange);

    const poller = window.setInterval(refresh, 30000);

    return () => {
      active = false;
      window.removeEventListener(notificationEventName, handleChange);
      window.clearInterval(poller);
    };
  }, [refresh]);

  const unreadCount = useMemo(
    () => notifications.filter((item) => !item.read).length,
    [notifications]
  );

  const markRead = useCallback(
    async (notificationId) => {
      setNotifications((current) =>
        current.map((item) =>
          item.id === notificationId ? { ...item, read: true } : item
        )
      );

      try {
        await markNotificationRead(notificationId);
      } catch (err) {
        await refresh();
        throw err;
      }
    },
    [refresh]
  );

  const markAllRead = useCallback(async () => {
    setNotifications((current) =>
      current.map((item) => ({ ...item, read: true }))
    );

    try {
      await markAllNotificationsRead();
    } catch (err) {
      await refresh();
      throw err;
    }
  }, [refresh]);

  const remove = useCallback(
    async (notificationId) => {
      setNotifications((current) =>
        current.filter((item) => item.id !== notificationId)
      );

      try {
        await removeNotification(notificationId);
      } catch (err) {
        await refresh();
        throw err;
      }
    },
    [refresh]
  );

  const clearAll = useCallback(async () => {
    const previous = notifications;
    setNotifications([]);

    try {
      await clearNotifications();
    } catch (err) {
      setNotifications(previous);
      throw err;
    }
  }, [notifications]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    refresh,
    markRead,
    markAllRead,
    remove,
    clearAll,
  };
}
